import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Loader2, Book, Volume2, Mic } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useStorybookBySlug, StorybookPage } from '../hooks/usePublishedStorybooks';
import { StoryPageView } from '../components/StoryPage';
import { ReadingRecorder } from '../components/ReadingRecorder';
import { prefetchAudio, generateSpeech, createAudioUrl } from '../services/readAloudService';

// Cover Page Component - Same layout as story pages
const CoverPage: React.FC<{
  title: string;
  coverImageUrl?: string;
  description?: string;
  onStart: () => void;
}> = ({ title, coverImageUrl, description, onStart }) => (
  <div className="bg-white rounded-3xl shadow-2xl overflow-hidden border border-gray-100 max-w-4xl mx-auto">
    {/* Title at top (where color bar would be) */}
    <div className="p-4 md:p-6 text-center bg-gradient-to-r from-blue-50 to-indigo-50 border-b border-gray-100">
      <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
        {title}
      </h1>
      {description && (
        <p className="text-sm md:text-base text-gray-600 mt-2 max-w-2xl mx-auto line-clamp-2">
          {description}
        </p>
      )}
    </div>

    {/* Cover Image - Same aspect ratio as story pages */}
    <div className="aspect-[16/10] bg-gradient-to-br from-blue-50 to-indigo-50 relative">
      {coverImageUrl ? (
        <img
          src={coverImageUrl}
          alt={title}
          className="w-full h-full object-contain"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <Book className="w-32 h-32 text-blue-300" />
        </div>
      )}
    </div>

    {/* Start Reading Button below image */}
    <div className="p-6 md:p-8 text-center bg-gradient-to-b from-white to-gray-50">
      <button
        onClick={onStart}
        className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl hover:from-blue-700 hover:to-indigo-700 transition-all"
      >
        <Book className="w-6 h-6" />
        Start Reading
      </button>
    </div>
  </div>
);

export const StorybookReader: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { storybook, pages, loading, error } = useStorybookBySlug(slug);
  // -1 = cover page, 0+ = story pages
  const [currentPageIndex, setCurrentPageIndex] = useState(-1);
  const [direction, setDirection] = useState(0); // -1 for prev, 1 for next
  const [isReading, setIsReading] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(-1);
  const [isRecorderOpen, setIsRecorderOpen] = useState(false);
  const [isRecordingActive, setIsRecordingActive] = useState(false);
  
  // Audio ref for read-aloud
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const isCoverPage = currentPageIndex === -1;
  const currentPage = isCoverPage ? null : pages[currentPageIndex];
  const totalPages = pages.length;
  const isFirstPage = currentPageIndex === -1;
  const isLastPage = currentPageIndex === totalPages - 1;

  // Prefetch audio for better UX
  // On cover page: prefetch page 1
  // On any page: prefetch next page
  useEffect(() => {
    if (pages.length === 0) return;
    
    const prefetchNextAudio = () => {
      if (isCoverPage) {
        // On cover page, prefetch the first story page
        const firstPage = pages[0];
        if (firstPage?.text) {
          console.log('[Reader] Prefetching page 1 audio from cover');
          prefetchAudio(firstPage.text, storybook?.title);
        }
      } else if (currentPageIndex < pages.length - 1) {
        // On a story page, prefetch the next page
        const nextPage = pages[currentPageIndex + 1];
        if (nextPage?.text) {
          console.log(`[Reader] Prefetching page ${currentPageIndex + 2} audio`);
          prefetchAudio(nextPage.text, storybook?.title);
        }
      }
    };
    
    // Small delay to not compete with page rendering
    const timer = setTimeout(prefetchNextAudio, 500);
    return () => clearTimeout(timer);
  }, [currentPageIndex, isCoverPage, pages, storybook?.title]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft' && !isFirstPage) {
        goToPrevPage();
      } else if (e.key === 'ArrowRight' && !isLastPage) {
        goToNextPage();
      } else if (e.key === 'Escape') {
        navigate(`/storybooks/${slug}`);
      } else if (e.key === 'Enter' && isCoverPage) {
        goToNextPage();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isFirstPage, isLastPage, isCoverPage, slug, navigate]);

  const goToPrevPage = useCallback(() => {
    if (!isFirstPage) {
      setDirection(-1);
      setCurrentPageIndex(prev => prev - 1);
      setCurrentWordIndex(-1);
    }
  }, [isFirstPage]);

  const goToNextPage = useCallback(() => {
    if (!isLastPage) {
      setDirection(1);
      setCurrentPageIndex(prev => prev + 1);
      setCurrentWordIndex(-1);
    }
  }, [isLastPage]);

  const startReading = useCallback(() => {
    setDirection(1);
    setCurrentPageIndex(0);
  }, []);

  // Read aloud functionality
  const handleReadAloud = async () => {
    if (!currentPage?.text || isReading) return;
    
    setIsReading(true);
    setCurrentWordIndex(0);
    
    try {
      const result = await generateSpeech(currentPage.text, storybook?.title);
      const audioUrl = createAudioUrl(result.audioContent, result.mimeType);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      // Simple word timing based on audio duration
      audio.onloadedmetadata = () => {
        const words = currentPage.text.split(/\s+/).filter(w => w.length > 0);
        const wordDuration = (audio.duration * 1000) / words.length;
        
        let wordIndex = 0;
        const interval = setInterval(() => {
          if (wordIndex < words.length) {
            setCurrentWordIndex(wordIndex);
            wordIndex++;
          } else {
            clearInterval(interval);
          }
        }, wordDuration);
        
        audio.onended = () => {
          clearInterval(interval);
          setIsReading(false);
          setCurrentWordIndex(-1);
        };
      };
      
      audio.play();
    } catch (err) {
      console.error('Read aloud error:', err);
      setIsReading(false);
      setCurrentWordIndex(-1);
    }
  };

  const stopReadAloud = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setIsReading(false);
    setCurrentWordIndex(-1);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (audioRef.current) audioRef.current.pause();
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-blue-500 mx-auto mb-4" />
          <p className="text-gray-600">Loading storybook...</p>
        </div>
      </div>
    );
  }

  if (error || !storybook || pages.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Book className="w-8 h-8 text-red-500" />
          </div>
          <p className="text-red-600 mb-4">
            {error || 'No pages found in this storybook'}
          </p>
          <Link 
            to="/storybooks" 
            className="text-blue-600 hover:text-blue-700 font-medium"
          >
            ← Back to Library
          </Link>
        </div>
      </div>
    );
  }

  const slideVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0
    }),
    center: {
      x: 0,
      opacity: 1
    },
    exit: (direction: number) => ({
      x: direction > 0 ? '-100%' : '100%',
      opacity: 0
    })
  };

  return (
    <div className="h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex flex-col overflow-hidden">
      {/* Header - Clean with Library link on left */}
      <header className="bg-white/80 backdrop-blur-sm border-b border-gray-100 flex-shrink-0 z-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {/* Library Link */}
            <Link 
              to="/storybooks"
              className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <Book className="w-5 h-5" />
              <span className="font-medium">Library</span>
            </Link>
          </div>

          {/* Title and Page Info */}
          <div className="text-center">
            <h1 className="font-bold text-gray-900 line-clamp-1">
              {storybook.title}
            </h1>
            <p className="text-sm text-gray-500">
              {isCoverPage ? 'Cover' : `Page ${currentPageIndex + 1} of ${totalPages}`}
            </p>
          </div>

          {/* Spacer for balance */}
          <div className="w-20" />
        </div>
      </header>

      {/* Main Reader Area - flex-1 with min-h-0 for proper scrolling */}
      <main className="flex-1 min-h-0 flex items-center justify-center px-4 py-4 overflow-auto">
        <div className="w-full max-w-5xl relative flex items-center">
          {/* Page Content */}
          <AnimatePresence mode="wait" custom={direction}>
            <motion.div
              key={currentPageIndex}
              custom={direction}
              variants={slideVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="w-full"
            >
              {isCoverPage ? (
                <CoverPage
                  title={storybook.title}
                  coverImageUrl={storybook.coverImageUrl || pages[0]?.imageUrl || undefined}
                  description={storybook.description || undefined}
                  onStart={startReading}
                />
              ) : currentPage && (
                <StoryPageView
                  page={currentPage}
                  turnCodes={storybook.turnCodes}
                  colorMappings={storybook.colorMappings}
                  phase={storybook.phase as '1' | '2'}
                  pageNumber={currentPageIndex + 1}
                  totalPages={totalPages}
                  currentWordIndex={-1}
                  isFirstPage={false}
                  storyTitle={storybook.title}
                  enabledColors={storybook.enabledColors ? new Set(storybook.enabledColors) : undefined}
                />
              )}
            </motion.div>
          </AnimatePresence>

          {/* Right Side Controls - Vertical Stack: Listen > Next > Record */}
          <div className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 md:translate-x-20 flex flex-col items-center gap-3">
            {/* Listen Button - Only on story pages */}
            {!isCoverPage && (
              <button
                onClick={isReading ? stopReadAloud : handleReadAloud}
                className={`p-3 rounded-2xl shadow-lg transition-all border-2 ${
                  isReading
                    ? 'bg-blue-500 border-blue-500 text-white animate-pulse'
                    : 'bg-white border-gray-200 text-gray-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600'
                }`}
                title={isReading ? "Stop reading" : "Listen to page"}
              >
                <Volume2 className="w-6 h-6" />
              </button>
            )}
            
            {/* Next Page Button - Large and colored */}
            <button
              onClick={goToNextPage}
              disabled={isLastPage}
              className={`p-4 rounded-2xl shadow-lg transition-all ${
                isLastPage
                  ? 'bg-gray-100 text-gray-300 cursor-not-allowed border-2 border-gray-200'
                  : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 hover:shadow-xl'
              }`}
              title="Next page"
            >
              <ChevronRight className="w-8 h-8" />
            </button>

            {/* Record Button - Only on story pages */}
            {!isCoverPage && storybook && (
              <button
                onClick={() => setIsRecorderOpen(!isRecorderOpen)}
                className={`p-3 rounded-2xl shadow-lg transition-all border-2 relative ${
                  isRecordingActive
                    ? 'bg-red-500 border-red-500 text-white'
                    : isRecorderOpen
                      ? 'bg-red-100 border-red-300 text-red-600'
                      : 'bg-white border-gray-200 text-gray-600 hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                }`}
                title={isRecordingActive ? "Recording in progress" : "Record reading"}
              >
                <Mic className="w-6 h-6" />
                {/* Solid glow ring when recording */}
                {isRecordingActive && (
                  <div className="absolute inset-0 rounded-2xl border-2 border-red-500 shadow-[0_0_12px_rgba(239,68,68,0.8)]" />
                )}
              </button>
            )}

            {/* Reading Recorder Panel - embedded below the button */}
            {storybook && !isCoverPage && (
              <ReadingRecorder
                storybookId={storybook.id}
                storybookTitle={storybook.title}
                currentPage={currentPageIndex + 1}
                totalPages={totalPages}
                embedded={true}
                isOpen={isRecorderOpen}
                onOpenChange={setIsRecorderOpen}
                onRecordingStateChange={setIsRecordingActive}
              />
            )}
          </div>

          {/* Left Side - Previous Button */}
          <button
            onClick={goToPrevPage}
            disabled={isFirstPage}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 md:-translate-x-20 p-4 rounded-2xl shadow-lg transition-all ${
              isFirstPage
                ? 'bg-gray-100 text-gray-300 cursor-not-allowed border-2 border-gray-200'
                : 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white hover:from-blue-600 hover:to-indigo-600 hover:shadow-xl'
            }`}
            title="Previous page"
          >
            <ChevronLeft className="w-8 h-8" />
          </button>
        </div>
      </main>

      {/* Page Dots Navigation - Always visible footer */}
      <footer className="bg-white/80 backdrop-blur-sm border-t border-gray-100 py-4 flex-shrink-0">
        <div className="max-w-7xl mx-auto px-4 flex justify-center gap-2 flex-wrap items-center">
          {/* Cover Page Dot */}
          <button
            onClick={() => {
              setDirection(-1);
              setCurrentPageIndex(-1);
              setCurrentWordIndex(-1);
            }}
            className={`w-4 h-4 rounded-md transition-all ${
              currentPageIndex === -1
                ? 'bg-blue-600 scale-110'
                : 'bg-gray-300 hover:bg-gray-400'
            }`}
            title="Cover"
          />
          <span className="w-px h-4 bg-gray-300 mx-1" />
          {/* Story Page Dots */}
          {pages.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > currentPageIndex ? 1 : -1);
                setCurrentPageIndex(index);
                setCurrentWordIndex(-1);
              }}
              className={`w-3 h-3 rounded-full transition-all ${
                index === currentPageIndex
                  ? 'bg-blue-600 scale-125'
                  : 'bg-gray-300 hover:bg-gray-400'
              }`}
              title={`Page ${index + 1}`}
            />
          ))}
        </div>
      </footer>

    </div>
  );
};

export default StorybookReader;
